import React, {
    ComponentPropsWithoutRef,
    ForwardedRef,
    forwardRef,
} from 'react';
import { UseFormRegister } from 'react-hook-form';
import { FormErrorText } from '@/components/FormErrorText';

type InputProps<T = undefined> = Omit<
    ComponentPropsWithoutRef<'input'>,
    'name' | 'id'
> & {
    label?: string;
    error?: string;
    inputClassName?: string;
    containerClassName?: string;
} & (
        | UseFormRegister<T>
        | {
              name: string;
              onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
          }
    );

/**
 * Input component. Use register if used together with React-hook-form.  Use as a standalone input with use of onChange (and no register)
 * The generic type T is the type of the form state object in react hook form. A property name of this object is equal to a property name of the form object.
 *
 * @param name - The id of the input to bound op to label. Is also used as the property name in a form object of react-hook-form
 * @param label
 * @param error
 * @param inputClassName
 * @param containerClassName
 * @param rest - Other props passed to the native input.
 * @param ref
 */
const InputPlain = <T extends Record<string, any>>(
    {
        name,
        label,
        error,
        inputClassName,
        containerClassName,
        ...rest
    }: InputProps<T>,
    ref: ForwardedRef<HTMLInputElement>,
) => {
    const classesInput = `form-control mb-1 ${inputClassName}`;
    const classesContainer = `flex flex-col ${containerClassName}`;

    return (
        <div className={`py-2 ${classesContainer}`}>
            {label && (
                <label htmlFor={name} className="form-label">
                    {label}
                </label>
            )}
            <div className="form-group">
                <input
                    id={name}
                    name={name}
                    className={classesInput}
                    ref={ref}
                    {...rest} // Spread standard input attributes
                />
                {error && <FormErrorText message={error} />}
            </div>
        </div>
    );
};

export const Input = forwardRef(InputPlain) as <T extends Record<string, any>>(
    props: InputProps<T> & { ref?: ForwardedRef<HTMLInputElement> },
) => React.ReactNode;
